import { useEffect, useMemo, useState, memo, useCallback } from 'react';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import { cn } from '@/utils/cn';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleDomBubbleEvent } from '@/utils/dom';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { SearchIcon } from 'lucide-react';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import {
  PatchUserLabelChatInputSchema,
  TPatchUserLabelChatInput,
  usePatchUserLabelChatOptimistically,
} from '@/features/cases/label-chat/api/patch-user-label-chat.api';
import { AxiosErrorCustom, axiosExtractErrorMessage } from '@/lib/axios';
import { useGetListLabelChatQuery } from '@/features/cases/label-chat/api/get-list-label-chat.api';
import { TLabelChat } from '@/features/cases/label-chat/types/label-chat.type';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import { Input } from '@/components/_ui/input';

// Type definition for label chat structure
type LabelChat = {
  labelChatId?: string;
  autoLabelId?: string;
  name: string;
  color: string;
  isAutoLabel: boolean;
};

export type TAssignLabelChatModalType = 'AssignLabelChatModal';
export interface IAssignLabelChatModalProps {
  caseId: string;
  messageId: string;
  labelChats?: {
    autoLabelId?: string;
    labelChatId?: string;
    name: string;
    color: string;
    isAutoLabel: boolean;
  }[];
}

/**
 * AssignLabelChatModal Component
 *
 * This modal allows users to assign or remove chat labels from a message.
 * Features:
 * - Search through available chat labels
 * - Select/deselect multiple labels with checkboxes
 * - Visual color indicators for each label
 * - Pre-populated with existing labels when editing
 * - Support for auto labels (read-only)
 */
const AssignLabelChatModal = memo((props: IAssignLabelChatModalProps) => {
  const showToast = useCustomToast();
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();

  const form = useForm<TPatchUserLabelChatInput>({
    mode: 'onChange',
    resolver: zodResolver(PatchUserLabelChatInputSchema),
  });

  // Check if modal is open
  const isOpenAssignLabelChatModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('AssignLabelChatModal');
  }, [caseModuleModalRootActions]);

  // Local state for search functionality and selected labels
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedLabelChats, setSelectedLabelChats] = useState<LabelChat[]>([]);

  // Initialize selected labels when modal opens with default values
  useEffect(() => {
    if (props?.labelChats || props?.caseId || props?.messageId) {
      // Map default values to our local state structure
      const initialSelectedLabels =
        props?.labelChats?.map((defaultLabel) => ({
          labelChatId: defaultLabel.labelChatId || '',
          autoLabelId: defaultLabel.autoLabelId || '',
          name: defaultLabel.name,
          color: defaultLabel.color,
          isAutoLabel: defaultLabel.isAutoLabel,
        })) || [];

      form.reset({
        caseId: props.caseId,
        messageId: props.messageId,
        labelChats: initialSelectedLabels
          .filter((label) => label.isAutoLabel === false)
          .map((label) => ({
            labelChatId: label?.labelChatId || '',
            name: label?.name,
            color: label?.color,
          })),
      });
      setSelectedLabelChats(initialSelectedLabels);
    }
  }, [props, form]);

  // Fetch all available chat labels from API
  const { data: labelChatData, isLoading: _isLoadingLabelChat } = useGetListLabelChatQuery({
    params: {
      page: 1,
      limit: 500, // Fetch a large number to show all available labels
      search: '',
      sortBy: 'createdAt',
      sortDir: 'DESC',
    },
    queryConfig: {
      enabled: isOpenAssignLabelChatModal,
    },
  });

  // Extract label options from API response
  const availableLabelOptions = useMemo(() => {
    const filteredLabelChats = labelChatData?.payload?.rows?.filter((item: TLabelChat) =>
      item?.name?.toLowerCase().includes(searchQuery?.toLowerCase())
    );

    const currentAutoLabel = selectedLabelChats.find((label) => label.isAutoLabel);

    return [
      ...(currentAutoLabel ? [currentAutoLabel] : []),
      ...(filteredLabelChats?.map((item) => ({
        ...item,
        isAutoLabel: false,
      })) || []),
    ];
  }, [labelChatData, searchQuery, selectedLabelChats]);

  /**
   * Handle selection/deselection of a chat label
   * @param labelToToggle - The chat label to toggle selection for
   */
  const handleLabelSelectionToggle = useCallback(
    (labelToToggle: LabelChat) => {
      if (labelToToggle.isAutoLabel) {
        return;
      }

      const isLabelCurrentlySelected = selectedLabelChats.some(
        (selectedLabel) => selectedLabel.labelChatId === labelToToggle.labelChatId
      );

      if (isLabelCurrentlySelected) {
        // Remove label from selection if already selected
        const updatedSelection = selectedLabelChats.filter(
          (selectedLabel) => selectedLabel.labelChatId !== labelToToggle.labelChatId
        );
        form.setValue(
          'labelChats',
          updatedSelection
            .map((label) => ({
              labelChatId: label.labelChatId || '',
              name: label.name,
              color: label.color,
            }))
            .filter((label) => label.labelChatId !== '')
        );
        setSelectedLabelChats(updatedSelection);
      } else {
        // Add label to selection if not currently selected
        const updatedSelection = [...selectedLabelChats, labelToToggle];
        form.setValue(
          'labelChats',
          updatedSelection
            .map((label) => ({
              labelChatId: label.labelChatId || '',
              name: label.name,
              color: label.color,
            }))
            .filter((label) => label.labelChatId !== '')
        );
        setSelectedLabelChats(updatedSelection);
      }
    },
    [selectedLabelChats, form]
  );

  /**
   * Check if a specific label is currently selected
   * @param labelToCheck - The chat label to check selection status for
   * @returns boolean indicating if the label is selected
   */
  const isLabelSelected = useCallback(
    (labelToCheck: LabelChat): boolean => {
      return selectedLabelChats.some((selectedLabel) => selectedLabel.labelChatId === labelToCheck.labelChatId);
    },
    [selectedLabelChats]
  );

  /**
   * Check if all available labels are currently selected
   * @returns boolean indicating if all labels are selected
   */
  const isAllSelected = useCallback((): boolean => {
    const filteredLabelAvailableChats = availableLabelOptions.filter((label) => label.labelChatId);
    const selectedLabelChatIds = selectedLabelChats.filter((label) => label.labelChatId);

    return filteredLabelAvailableChats.length > 0 && selectedLabelChatIds.length === filteredLabelAvailableChats.length;
  }, [availableLabelOptions, selectedLabelChats]);

  /**
   * Handle selection/deselection of all labels
   */
  const handleLabelSelectionAllToggle = useCallback(() => {
    if (isAllSelected()) {
      // If all are selected, deselect all
      form.setValue('labelChats', []);
      setSelectedLabelChats([...(selectedLabelChats.filter((label) => label.isAutoLabel) || [])]);
    } else {
      // If not all are selected, select all
      form.setValue(
        'labelChats',
        availableLabelOptions.map((label) => ({
          labelChatId: label.labelChatId || '',
          name: label.name,
          color: label.color,
        }))
      );
      setSelectedLabelChats([
        ...(selectedLabelChats.filter((label) => label.isAutoLabel) || []),
        ...(labelChatData?.payload?.rows?.map((label) => ({
          labelChatId: label.labelChatId || '',
          autoLabelId: undefined,
          name: label.name,
          color: label.color,
          isAutoLabel: false,
        })) || []),
      ]);
    }
  }, [isAllSelected, availableLabelOptions, selectedLabelChats, form, labelChatData]);

  // Handler Mutation
  const mutationPatchUserLabelChat = usePatchUserLabelChatOptimistically({
    mutationConfig: {
      onError: (error) => {
        const errorMsg = axiosExtractErrorMessage(error as AxiosErrorCustom);
        showToast({
          message: errorMsg,
          type: 'error',
        });
      },
      onSuccess: () => {
        showToast({
          message: 'Label chat assigned successfully',
          type: 'success',
        });
        handleClose();
      },
    },
  });

  const handleSubmitAssignLabelChat = useCallback(
    (data: TPatchUserLabelChatInput) => {
      mutationPatchUserLabelChat.mutate({
        caseId: data.caseId,
        messageId: data.messageId,
        labelChats: selectedLabelChats
          .map((label) => ({
            labelChatId: label.labelChatId || '',
            name: label.name,
            color: label.color,
          }))
          .filter((label) => label.labelChatId !== ''),
      });
    },
    [mutationPatchUserLabelChat, selectedLabelChats]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('AssignLabelChatModal');
    form.reset();
    setSelectedLabelChats([]);
  }, [caseModuleModalRootActions, form]);

  return (
    <Modal
      isOpen={isOpenAssignLabelChatModal}
      lockBodyScroll
      onClose={handleClose}
      isCloseOutside={false}
      className="w-[400px]"
    >
      <ModalHeader>
        <ModalTitle>Select Label Chat</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="flex flex-col gap-[12px]">
          {/* Search input for filtering labels */}
          <div>
            <Input
              startIcon={<SearchIcon className="w-[16px] h-[16px]" />}
              placeholder="Search label chat..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>

          {/* Scrollable container for label options */}
          <div className="flex flex-col border border-[#D9D9D9] rounded-[8px] overflow-hidden">
            {/* Sticky "Select All" header */}
            <div className="flex items-center justify-between gap-[20px] bg-[#0C8DBF] px-[16px] py-[12px] sticky top-0 z-10">
              <div className="flex items-center gap-[16px]">
                <Checkbox
                  className="border-white bg-white outline-hidden  data-[state=checked]:text-[#0C8DBF] data-[state=checked]:bg-white data-[state=checked]:border-[#0C8DBF] data-[state=unchecked]:text-white data-[state=unchecked]:bg-white data-[state=unchecked]:border-[#D9D9D9]"
                  checked={isAllSelected()}
                  onCheckedChange={handleLabelSelectionAllToggle}
                />
                <span className="text-[12px] font-normal text-white">Select All</span>
              </div>
              <div className="flex items-center gap-[12px]">
                <span className="text-[12px] font-normal text-white">
                  {selectedLabelChats.length} / {availableLabelOptions.length}
                </span>
              </div>
            </div>

            {/* Scrollable content area */}
            <div className="max-h-[200px] overflow-y-auto">
              {availableLabelOptions.map((labelOption) => {
                const isCurrentlySelected = isLabelSelected(labelOption);

                return (
                  <div
                    key={labelOption.labelChatId}
                    className="flex items-center gap-[20px] cursor-pointer hover:bg-gray-50   transition-colors px-[16px] py-[12px] border-b border-[#D9D9D9]"
                    onClick={() => {
                      if (!labelOption.isAutoLabel) {
                        handleLabelSelectionToggle(labelOption);
                      }
                      return;
                    }}
                  >
                    {/* Checkbox to show selection state */}
                    <Checkbox
                      checked={labelOption.isAutoLabel ? true : isCurrentlySelected}
                      disabled={labelOption.isAutoLabel}
                      // Note: Selection is handled via parent div onClick, not checkbox onChange
                    />

                    {/* Color indicator for the label */}
                    <div
                      className={cn(`w-[12px] h-[12px] rounded-full border border-[#${labelOption.color}]`)}
                      style={{ backgroundColor: labelOption.color }}
                    />

                    {/* Label name */}
                    <span className="text-[12px] font-normal flex-1">{labelOption.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <form
            id="assign-label-chat-form"
            onSubmit={(e) => {
              handleDomBubbleEvent(e);
              form.handleSubmit(handleSubmitAssignLabelChat)(e);
            }}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="assign-label-chat-form" type="submit" disabled={mutationPatchUserLabelChat.isPending}>
          {mutationPatchUserLabelChat.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Assign
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

AssignLabelChatModal.displayName = 'AssignLabelChatModal';
export default AssignLabelChatModal;
